<template>
  <div id="app"
       :class="{ 'ym-in-app': isAppWebView }"
  >
    <layout v-if="errorRequest == null"></layout>
    <error-page v-else
                type="loading-error"
                hint="加载失败"
    >
      <ym-button class="retry-btn"
                 plain
                 type="primary"
                 size="small"
                 @click="handleLoadingError"
      >
        点击重试
      </ym-button>
    </error-page>
  </div>
</template>

<script>
  import { Button } from '@ymba/ym-ui';
  import { setOrigin } from 'ym-reporter-vue';
  import { mapGetters, mapActions } from 'vuex';
  import pageBrowsePathMixin from '@/mixins/report/pageBrowsePath';
  import Layout from '@/layout/Layout';
  import ErrorPage from '@/components/ErrorPage';
  import {
    getAlertMessage,
    removeAlertMessage,
    setEventLogConfig,
    setAppRouteMap,
    getPlayTimeStorage,
    removeStoragePlayTimeRange,
    durationToTime,
    isAppWebView,
    isAndroid,
    limitFontSize,
  } from '@/utils';
  import {
    getAppStyle,
    getEventLogConfig,
    getAppRouteMap,
    getDownloadBarInfo,
    getAgencySettingBarInfo,
    getAgencyPageList,
  } from '@/services/app';
  import request from '@/utils/request';
  import event from '@/utils/bus';

  export default {
    name: 'App',
    metaInfo: {
      title: '少年商学院',
    },
    mixins: [pageBrowsePathMixin],
    components: {
      Layout,
      ErrorPage,
      'ym-button': Button,
    },
    data() {
      return {
        isAppWebView: isAppWebView(),
        feedbackTimer: null,
        timeNumber: 60,
      };
    },
    computed: {
      ...mapGetters([
        'errorRequest',
        'player',
      ]),
    },
    watch: {
      $route(val) {
        this.getAgencySettingBarInfo(val);
      },
    },
    methods: {
      ...mapActions(['LoginWithQueryToken']),
      showLocationAlertMessage() {
        const message = getAlertMessage();
        if (message && message !== '') {
          this.$dialog.alert({
            message,
            confirmButtonText: '知道了',
          });
          // 获取后清除弹窗信息
          removeAlertMessage();
        }
      },
      async setAppLogConfig() {
        const data = await getEventLogConfig();
        setEventLogConfig(data);
      },
      async setAppRouteMap() {
        const data = await getAppRouteMap();
        setAppRouteMap(data);
      },
      handleLoadingError() {
        const { errorRequest } = this;
        if (errorRequest == null) return;
        request(errorRequest);
      },
      listenWebviewLoginSucceed() {
        document.addEventListener('appWebviewLoginSucceed', async (e) => {
          const { detail: { mApiToken, mWApiToken, miniToken } } = e;
          console.log('listenWebviewLoginSucceed', mApiToken, mWApiToken, miniToken);
          await this.LoginWithQueryToken({ mApiToken, mWApiToken, miniToken });
          event.$emit('updateDetail');
        });
      },
      listenWebviewNeedReloadData() {
        document.addEventListener('appWebViewNeedReloadData', () => {
          event.$emit('reloadWebviewData');
        });
      },
      listenPageUnload() {
        window.addEventListener(this.isAppWebView && isAndroid() ? 'beforeunload' : 'pagehide', () => {
          event.$emit('pagehide');
          this.pageBrowsePathHandler();
          this.destroyPlayer();
        });
      },
      destroyPlayer() {
        // 播放器暂停
        this.player.pause();
        // 播放进度
        const playTime = getPlayTimeStorage();
        if (playTime != null) {
          Object.keys(playTime).forEach(async (key) => {
            const keys = key.split('-');
            const [type, id] = keys;
            const item = playTime[key];
            const { range, duration } = item;
            if (duration && range && range.length > 0) {
              const params = {
                id: Number(id),
                type,
                duration: durationToTime(duration),
                range,
              };
              // 发送打点
              await this.player.logPlayProgress(id, params);
              // 删除已播时间段记录
              removeStoragePlayTimeRange(id, type);
            }
          });
        }
      },
      async fetchDownloadBarInfo() {
        const data = await getDownloadBarInfo();
        if (data.downloadBarInfo) {
          this.$store.dispatch('toggleDownloadBarInfo', data.downloadBarInfo);
          this.$store.dispatch('toggleDownloadBarKey', data.downloadBarInfo.key);
        }
      },
      async setAppStyle() {
        const data = await getAppStyle();
        const { style } = data;
        if (style === 'gray') document.body.classList.add('is-gray');
        if (this.isAppWebView) document.body.classList.add('ym-in-app');
      },
      async getAgencySettingBarInfo(val) {
        const { name } = val;
        const pageMap = await getAgencyPageList();
        const showBar = Boolean(pageMap.list.indexOf(name) !== -1);
        if (!showBar) {
          this.$store.dispatch('toggleAgencySettingBar', {
            show: false,
          });
          return;
        }
        const { params } = val;
        const { id } = params;
        const type = (name.substring(0, (name.length - 6))).toLowerCase();
        let productType = type.indexOf('combo') !== -1 ? type.substring(type.length - 5, type.length) : type;
        if (productType === 'millionreadvippresale' || name.toLowerCase() === 'millionreadselect') {
          productType = 'millionreadvip';
        }
        const productId = name.toLowerCase() === 'millionreadselect' ? 2 : id;
        const data = await getAgencySettingBarInfo(productId, productType);
        this.$store.dispatch('toggleAgencySettingBar', data);
      },
      // 监听反馈倒计时
      listenFeedbackTimeEnd() {
        if (this.feedbackTimer) {
          return;
        }
        sessionStorage.setItem('feedbackDisabled', true);
        this.feedbackTimer = setInterval(() => {
          if (this.timeNumber == 0) {
            clearInterval(this.feedbackTimer);
            this.feedbackTimer = null;
            this.timeNumber = 60;
            sessionStorage.setItem('feedbackDisabled', '');
            return;
          }
          this.timeNumber--;
          console.log(`倒计时：${this.timeNumber}`);
        }, 1000);
      },
    },

    created() {
      // 设置样式
      this.setAppStyle();
      // 记录页面来源
      setOrigin();
      // 展示location 弹窗信息
      this.showLocationAlertMessage();
      // 更新打Log 配置
      this.setAppLogConfig();
      // 更新App 路由映射
      this.setAppRouteMap();
      // 监听浏览器刷新或关闭
      this.listenPageUnload();
      // 监听webview 登录成功
      this.listenWebviewLoginSucceed();
      // 监听webview 需要重载数据
      this.listenWebviewNeedReloadData();
      // 设置下载引导栏信息
      this.fetchDownloadBarInfo();
      // 安卓阻止字体大小调整
      if (isAndroid()) limitFontSize();

      // 监听反馈提交
      clearInterval(this.feedbackTimer);
      this.feedbackTimer = null;
      this.timeNumber = 60;
      sessionStorage.setItem('feedbackDisabled', '');
      event.$on('onFeedback', this.listenFeedbackTimeEnd);
    },

    destroyed() {
      event.$off('onFeedback');
      clearInterval(this.feedbackTimer);
      this.feedbackTimer = null;
      this.timeNumber = 60;
      sessionStorage.setItem('feedbackDisabled', '');
    },
  };
</script>

<style lang="less">
  @import '~@/assets/styles/index.less';

  #app {
    height: 100vh;
    height: 100%;
    font-size: @font-size-base;
  }

  .retry-btn {
    margin-top: @size-medium;
    border-radius: 35px;
  }
</style>
